import { useState } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { ShieldCheck, X } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { cn } from '@/utils/cn'
import type { PartnerView } from '@/components/Partners/partnerView.types'
import { PartnerDetailIndicacoesTab } from '@/components/Partners/Modals/PartnerDetailIndicacoesTab'
import { PARTNER_DETAIL_TABS, type PartnerDetailTabId } from '@/components/Partners/Modals/partnerDetailModalConstants'
import { PartnerDetailOverviewTab } from '@/components/Partners/Modals/PartnerDetailOverviewTab'
import { PartnerDetailDealsTab } from '@/components/Partners/Modals/PartnerDetailDealsTab'
import { PartnerDetailFinanceTab } from '@/components/Partners/Modals/PartnerDetailFinanceTab'
import { PartnerDetailNetworkTab } from '@/components/Partners/Modals/PartnerDetailNetworkTab'
import { PartnerPaymentModal, type PartnerPaymentFormData } from '@/components/Partners/Modals/PartnerPaymentModal'

interface PartnerDetailModalProps {
  partner: PartnerView | null
  onClose: () => void
  onUpdateCommissionStatus?: (commissionId: string, status: string) => void
  onCalculateCommission?: (partnerId: string, dealId: string) => void
  onAddPayment?: (data: { valor: number; metodo: string; notas: string; dataPagamento?: string; commissionIds?: string[] }) => void
  onRecurrenceAction?: (dealId: string, action: 'end' | 'reopen') => void
  onSelectPartner?: (partnerId: string) => void
  onApprovePortalAccess?: (partner: PartnerView) => void
  onEdit?: (partner: PartnerView) => void
}

const defaultPaymentData = (): PartnerPaymentFormData => ({
  valor: 0,
  metodo: 'Pix',
  notas: '',
  dataPagamento: new Date().toISOString().slice(0, 10),
})

export function PartnerDetailModal({
  partner,
  onClose,
  onUpdateCommissionStatus,
  onCalculateCommission,
  onAddPayment,
  onRecurrenceAction,
  onSelectPartner,
  onApprovePortalAccess,
  onEdit,
}: PartnerDetailModalProps) {
  const [activeTab, setActiveTab] = useState<PartnerDetailTabId>('overview')
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [paymentData, setPaymentData] = useState<PartnerPaymentFormData>(defaultPaymentData)

  if (!partner) return null

  const handlePaymentSubmit = () => {
    const commissionIds = (partner.comissoes ?? [])
      .filter((c) => c.status === 'aprovada')
      .map((c) => c.id)

    onAddPayment?.({ ...paymentData, commissionIds })
    setShowPaymentModal(false)
    setPaymentData(defaultPaymentData())
  }

  const openPaymentModal = () => {
    setPaymentData({
      ...defaultPaymentData(),
      valor: partner.stats?.totalToPay ?? 0,
    })
    setShowPaymentModal(true)
  }
  const pendingPortalValidation = !partner.portalUserId
  const commercialLabel = partner.responsavelUser?.name ?? 'será distribuído automaticamente'

  return createPortal(
    <AnimatePresence>
      <div className='fixed inset-0 z-50 flex items-center justify-center p-4'>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className='absolute inset-0 bg-black/60 backdrop-blur-sm'
          onClick={onClose}
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 8 }}
          transition={{ duration: 0.15, ease: 'easeOut' }}
          className='relative w-full max-w-5xl max-h-[90vh] z-10 bg-[var(--bg-card)] border border-[var(--glass-border)] rounded-xl shadow-2xl flex flex-col overflow-hidden'
        >
          <div className='flex items-center justify-between p-5 border-b border-[var(--glass-border)]'>
            <div>
              <h2 className='font-semibold text-[var(--font-color-h2)]' style={{ fontSize: 'var(--font-size-h3)' }}>
                {partner.nome}
              </h2>
              <p className='text-[var(--font-color-muted)]' style={{ fontSize: 'var(--font-size-sm)' }}>
                {partner.empresa ?? 'Sem empresa vinculada'}
                {partner.responsavelUser?.name ? ` · Comercial ${partner.responsavelUser.name}` : ''}
              </p>
            </div>
            <button onClick={onClose} className='p-1.5 rounded-md text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--glass-bg)] transition-colors'>
              <X size={16} />
            </button>
          </div>

          <div className='flex border-b border-[var(--glass-border)] px-5'>
            {PARTNER_DETAIL_TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'px-4 py-3 text-[13px] font-medium transition-colors relative',
                  activeTab === tab.id
                    ? 'text-[var(--brand-primary)]'
                    : 'text-[var(--text-muted)] hover:text-[var(--text-primary)]'
                )}
              >
                {tab.label}
                {activeTab === tab.id && (
                  <motion.div layoutId='partner-tab-indicator' className='absolute bottom-0 left-0 right-0 h-0.5 bg-[var(--brand-primary)]' />
                )}
              </button>
            ))}
          </div>

          <div className='min-h-0 flex-1 overflow-y-auto p-5'>
            {pendingPortalValidation && (
              <div className='mb-5 rounded-2xl border border-amber-500/20 bg-amber-500/10 p-4 text-amber-100'>
                <div className='flex flex-col gap-4 md:flex-row md:items-center md:justify-between'>
                  <div className='flex gap-3'>
                    <div className='mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-500/15 text-amber-300'>
                      <ShieldCheck size={18} />
                    </div>
                    <div>
                      <p className='text-sm font-bold'>Validação para liberar Portal do Parceiro</p>
                      <p className='mt-1 text-xs text-amber-100/75'>
                        Revise nome, empresa, e-mail e telefone antes de aprovar. A aprovação vincula um comercial pelo rodízio quando faltar, cria o acesso e envia o convite.
                      </p>
                      <div className='mt-3 grid gap-1 text-xs text-amber-100/80 sm:grid-cols-2'>
                        <span>E-mail: {partner.email ?? 'não informado'}</span>
                        <span>Telefone: {partner.telefone ?? 'não informado'}</span>
                        <span>Empresa: {partner.empresa ?? 'não informada'}</span>
                        <span>Comercial: {commercialLabel}</span>
                      </div>
                    </div>
                  </div>
                  <div className='flex flex-col gap-2 sm:flex-row sm:items-center'>
                    {onEdit && (
                      <Button variant='secondary' size='sm' onClick={() => onEdit(partner)}>
                        Editar informações
                      </Button>
                    )}
                    {onApprovePortalAccess && (
                      <Button variant='primary' size='sm' onClick={() => onApprovePortalAccess(partner)}>
                        Aprovar e enviar convite
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}
            {activeTab === 'overview' && <PartnerDetailOverviewTab partner={partner} />}
            {activeTab === 'indicacoes' && <PartnerDetailIndicacoesTab partner={partner} />}
            {activeTab === 'network' && (
              <PartnerDetailNetworkTab partner={partner} onSelectPartner={onSelectPartner} />
            )}
            {activeTab === 'deals' && (
              <PartnerDetailDealsTab
                partner={partner}
                onCalculateCommission={onCalculateCommission}
                onRecurrenceAction={onRecurrenceAction}
              />
            )}
            {activeTab === 'finance' && (
              <PartnerDetailFinanceTab
                partner={partner}
                onUpdateCommissionStatus={onUpdateCommissionStatus}
                onOpenPaymentModal={openPaymentModal}
              />
            )}
          </div>
        </motion.div>

        <PartnerPaymentModal
          open={showPaymentModal}
          paymentData={paymentData}
          setPaymentData={setPaymentData}
          onClose={() => setShowPaymentModal(false)}
          onSubmit={handlePaymentSubmit}
        />
      </div>
    </AnimatePresence>,
    document.body
  )
}
